import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import {
    Box,
    Divider,
    MenuItem,
    Paper,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    alpha,
    useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import {
    highlightPendingPlaceholders,
    stripPlaceholderHighlights,
} from '../utils/contractTextPlaceholders';

function preventToolbarFocusLoss(event) {
    event.preventDefault();
}

function exec(command, value = null) {
    document.execCommand(command, false, value);
}

function ToolbarButton({ title, onClick, children }) {
    return (
        <Tooltip title={title}>
            <ToggleButton value={title} onMouseDown={preventToolbarFocusLoss} onClick={onClick}>
                {children}
            </ToggleButton>
        </Tooltip>
    );
}

function toEditorHtml(html, highlightPlaceholders) {
    const base = html || '';
    return highlightPlaceholders ? highlightPendingPlaceholders(base) : base;
}

export default function RichTextEditor({
    value,
    onChange,
    placeholder,
    readOnly = false,
    minHeight = 320,
    highlightPlaceholders = false,
}) {
    const theme = useTheme();
    const editorRef = useRef(null);

    const focusEditor = useCallback(() => {
        editorRef.current?.focus();
    }, []);

    const emitChange = useCallback(() => {
        if (!editorRef.current || readOnly) return;
        const raw = editorRef.current.innerHTML;
        onChange?.(highlightPlaceholders ? stripPlaceholderHighlights(raw) : raw);
    }, [onChange, readOnly, highlightPlaceholders]);

    const handleBlur = useCallback(() => {
        if (!editorRef.current || readOnly) return;
        const clean = highlightPlaceholders
            ? stripPlaceholderHighlights(editorRef.current.innerHTML)
            : editorRef.current.innerHTML;
        onChange?.(clean);
        // Reaplica chips ao sair do campo (evita brigar com o cursor durante a digitação).
        if (highlightPlaceholders) {
            editorRef.current.innerHTML = highlightPendingPlaceholders(clean);
        }
    }, [onChange, readOnly, highlightPlaceholders]);

    const runCommand = useCallback(
        (command, commandValue = null) => {
            focusEditor();
            exec(command, commandValue);
            emitChange();
        },
        [emitChange, focusEditor],
    );

    const handleInsertLink = useCallback(() => {
        focusEditor();
        const url = window.prompt('Informe a URL do link:', 'https://');
        if (url) {
            exec('createLink', url);
            emitChange();
        }
    }, [emitChange, focusEditor]);

    const handleFormatBlock = useCallback(
        event => {
            runCommand('formatBlock', event.target.value);
        },
        [runCommand],
    );

    useEffect(() => {
        if (!editorRef.current) return;
        const nextValue = toEditorHtml(value, highlightPlaceholders);
        const currentClean = highlightPlaceholders
            ? stripPlaceholderHighlights(editorRef.current.innerHTML)
            : editorRef.current.innerHTML;
        const nextClean = highlightPlaceholders ? stripPlaceholderHighlights(nextValue) : nextValue;
        if (currentClean !== nextClean) {
            editorRef.current.innerHTML = nextValue;
        }
    }, [value, highlightPlaceholders]);

    return (
        <Paper variant="outlined">
            {!readOnly && (
                <>
                    <Box sx={{ px: 1, py: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                        <Select
                            size="small"
                            defaultValue="p"
                            onChange={handleFormatBlock}
                            onMouseDown={preventToolbarFocusLoss}
                            sx={{ minWidth: 120, height: 34 }}
                        >
                            <MenuItem value="p">Parágrafo</MenuItem>
                            <MenuItem value="h2">Título 2</MenuItem>
                            <MenuItem value="h3">Título 3</MenuItem>
                            <MenuItem value="h4">Título 4</MenuItem>
                        </Select>

                        <ToggleButtonGroup size="small">
                            <ToolbarButton title="Negrito" onClick={() => runCommand('bold')}>
                                <FormatBoldIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Itálico" onClick={() => runCommand('italic')}>
                                <FormatItalicIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Sublinhado" onClick={() => runCommand('underline')}>
                                <FormatUnderlinedIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Tachado" onClick={() => runCommand('strikeThrough')}>
                                <FormatStrikethroughIcon fontSize="small" />
                            </ToolbarButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup size="small">
                            <ToolbarButton title="Lista numerada" onClick={() => runCommand('insertOrderedList')}>
                                <FormatListNumberedIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Lista com marcadores" onClick={() => runCommand('insertUnorderedList')}>
                                <FormatListBulletedIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Citação" onClick={() => runCommand('formatBlock', 'blockquote')}>
                                <FormatQuoteIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Linha horizontal" onClick={() => runCommand('insertHorizontalRule')}>
                                <HorizontalRuleIcon fontSize="small" />
                            </ToolbarButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup size="small">
                            <ToolbarButton title="Alinhar à esquerda" onClick={() => runCommand('justifyLeft')}>
                                <FormatAlignLeftIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Centralizar" onClick={() => runCommand('justifyCenter')}>
                                <FormatAlignCenterIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Alinhar à direita" onClick={() => runCommand('justifyRight')}>
                                <FormatAlignRightIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Justificar" onClick={() => runCommand('justifyFull')}>
                                <FormatAlignJustifyIcon fontSize="small" />
                            </ToolbarButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup size="small">
                            <ToolbarButton title="Aumentar recuo" onClick={() => runCommand('indent')}>
                                <FormatIndentIncreaseIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Diminuir recuo" onClick={() => runCommand('outdent')}>
                                <FormatIndentDecreaseIcon fontSize="small" />
                            </ToolbarButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup size="small">
                            <ToolbarButton title="Inserir link" onClick={handleInsertLink}>
                                <LinkIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Remover link" onClick={() => runCommand('unlink')}>
                                <LinkOffIcon fontSize="small" />
                            </ToolbarButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup size="small">
                            <ToolbarButton title="Desfazer" onClick={() => runCommand('undo')}>
                                <UndoIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Refazer" onClick={() => runCommand('redo')}>
                                <RedoIcon fontSize="small" />
                            </ToolbarButton>
                            <ToolbarButton title="Limpar formatação" onClick={() => runCommand('removeFormat')}>
                                <FormatClearIcon fontSize="small" />
                            </ToolbarButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Divider />
                </>
            )}

            <Box
                ref={editorRef}
                contentEditable={!readOnly}
                suppressContentEditableWarning
                onInput={emitChange}
                onBlur={handleBlur}
                data-placeholder={placeholder}
                sx={{
                    minHeight,
                    px: 2,
                    py: 1.5,
                    outline: 'none',
                    fontFamily: theme.typography.body1.fontFamily,
                    fontSize: theme.typography.body1.fontSize,
                    lineHeight: 1.6,
                    color: 'text.primary',
                    '&:empty:before': {
                        content: 'attr(data-placeholder)',
                        color: 'text.disabled',
                    },
                    '& h2': {
                        mt: 1,
                        mb: 0.5,
                        textAlign: 'center',
                        fontSize: '1.15rem',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                    },
                    '& h3': {
                        mt: 2.5,
                        mb: 1,
                        fontSize: '1rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        pb: 0.5,
                    },
                    '& p': {
                        mb: 1.25,
                        textAlign: 'justify',
                    },
                    '& ol, & ul': {
                        mb: 1.5,
                        pl: 3,
                    },
                    '& li': {
                        mb: 0.5,
                    },
                    '& hr': {
                        my: 3,
                        border: 0,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    },
                    '& blockquote': {
                        borderLeft: '3px solid',
                        borderColor: 'divider',
                        pl: 2,
                        ml: 0,
                        color: 'text.secondary',
                    },
                    '& a': {
                        color: 'primary.main',
                    },
                    '& mark.contract-placeholder-pending': {
                        display: 'inline',
                        color: theme.palette.error.dark,
                        backgroundColor: alpha(theme.palette.error.main, 0.12),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.45)}`,
                        borderRadius: '999px',
                        padding: '1px 6px',
                        margin: '0 2px',
                        fontSize: '0.85em',
                        fontWeight: 700,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        lineHeight: 1.4,
                        boxDecorationBreak: 'clone',
                        WebkitBoxDecorationBreak: 'clone',
                    },
                }}
            />
        </Paper>
    );
}
